import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useReducer, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getAddonsLlmKeysQueryKey,
  useAddonsLlmKeysCreate,
  useAddonsLlmKeysDelete,
} from '@newfold/huapi-js';

import { useAlert } from '~/components/Alerts/alertsStore';

import { isValidApiKeyName } from '../utils/helpers';
import {
  AiApiKey,
  ApiKeysModalAction,
  ApiKeysModalState,
  UseApiKeysModalArgs,
  UseApiKeysModalOptions,
} from '../utils/types';

const createInitialState = (apiKeys: AiApiKey[]): ApiKeysModalState => ({
  view: 'list',
  keys: apiKeys,
  newKeyName: '',
  generatedKey: '',
  pendingDelete: null,
});

const apiKeysModalReducer = (
  state: ApiKeysModalState,
  action: ApiKeysModalAction,
): ApiKeysModalState => {
  switch (action.type) {
    case 'reset':
      return createInitialState(action.payload.apiKeys);
    case 'syncKeys':
      return { ...state, keys: action.payload.apiKeys };
    case 'goToList':
      return { ...state, view: 'list' };
    case 'goToCreate':
      return { ...state, view: 'create', newKeyName: '' };
    case 'cancelCreate':
      return { ...state, view: 'list', newKeyName: '' };
    case 'setNewKeyName':
      return { ...state, newKeyName: action.payload.value };
    case 'submitCreateSuccess':
      return {
        ...state,
        generatedKey: action.payload.generatedKey,
        newKeyName: '',
        view: 'save',
      };
    case 'startDelete':
      return {
        ...state,
        pendingDelete: action.payload.key,
        view: 'delete',
      };
    case 'confirmDelete': {
      if (!state.pendingDelete) {
        return state;
      }

      return {
        ...state,
        keys: state.keys.filter((key) => key.id !== state.pendingDelete?.id),
        pendingDelete: null,
        view: 'list',
      };
    }
    case 'cancelDelete':
      return {
        ...state,
        pendingDelete: null,
        view: 'list',
      };
    default:
      return state;
  }
};

const useApiKeysModal = (
  open: boolean,
  apiKeys: AiApiKey[],
  options: UseApiKeysModalOptions = {},
): UseApiKeysModalArgs => {
  const { addonId } = options;
  const { t } = useTranslation('aiTooling', { keyPrefix: 'apiKeys.modal' });
  const generateAlert = useAlert();
  const queryClient = useQueryClient();

  const [state, dispatch] = useReducer(
    apiKeysModalReducer,
    apiKeys,
    createInitialState,
  );
  const wasOpenRef = useRef(open);
  const apiKeysRef = useRef(apiKeys);
  apiKeysRef.current = apiKeys;

  const invalidateKeys = async () => {
    if (!addonId) {
      return;
    }

    await queryClient.invalidateQueries({
      queryKey: getAddonsLlmKeysQueryKey(addonId),
    });
  };

  const { mutate: createKey, isPending: isCreating } = useAddonsLlmKeysCreate({
    mutation: {
      onSuccess: (response) => {
        const generatedKey = response.data?.virtual_key ?? '';
        dispatch({
          type: 'submitCreateSuccess',
          payload: { generatedKey },
        });
        invalidateKeys();
      },
      onError: () => {
        generateAlert({
          severity: 'error',
          description: t('create.error'),
        });
      },
    },
  });

  const { mutate: deleteKey, isPending: isDeleting } = useAddonsLlmKeysDelete({
    mutation: {
      onSuccess: async () => {
        dispatch({ type: 'confirmDelete' });
        await invalidateKeys();
      },
      onError: () => {
        generateAlert({
          severity: 'error',
          description: t('delete.error'),
        });
      },
    },
  });

  const apiKeysSignature = apiKeys.map((key) => key.id).join('|');

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      dispatch({ type: 'reset', payload: { apiKeys: apiKeysRef.current } });
    }

    wasOpenRef.current = open;
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    dispatch({ type: 'syncKeys', payload: { apiKeys: apiKeysRef.current } });
  }, [open, apiKeysSignature]);

  const goToList = () => {
    dispatch({ type: 'syncKeys', payload: { apiKeys: apiKeysRef.current } });
    dispatch({ type: 'goToList' });
  };
  const goToCreate = () => dispatch({ type: 'goToCreate' });
  const cancelCreate = () => dispatch({ type: 'cancelCreate' });
  const setNewKeyName = (value: string) =>
    dispatch({ type: 'setNewKeyName', payload: { value } });

  const submitCreate = () => {
    const trimmedName = state.newKeyName.trim();
    if (!isValidApiKeyName(trimmedName) || !addonId) {
      return;
    }

    createKey({
      addonId,
      data: { key_name: trimmedName },
    });
  };

  const startDelete = (key: AiApiKey) =>
    dispatch({ type: 'startDelete', payload: { key } });

  const confirmDelete = () => {
    if (!state.pendingDelete || !addonId) {
      return;
    }

    deleteKey({
      addonId,
      data: { key_name: state.pendingDelete.name },
    });
  };

  const cancelDelete = () => dispatch({ type: 'cancelDelete' });

  return {
    view: state.view,
    keys: state.keys,
    newKeyName: state.newKeyName,
    setNewKeyName,
    generatedKey: state.generatedKey,
    pendingDelete: state.pendingDelete,
    isCreating,
    isDeleting,
    goToList,
    goToCreate,
    cancelCreate,
    submitCreate,
    startDelete,
    confirmDelete,
    cancelDelete,
  };
};

export default useApiKeysModal;
